import React, { Component, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, ExpandingTextarea } from '../../components';
import { useField } from 'react-final-form';
import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

// Open eye: shown while the password is masked (click to reveal).
const IconEyeOpen = () => (
  <svg
    className={css.eyeIcon}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <g
      fill="none"
      fillRule="evenodd"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.7 12S5.4 5.5 12 5.5 22.3 12 22.3 12 18.6 18.5 12 18.5 1.7 12 1.7 12z" />
      <circle cx="12" cy="12" r="3.2" />
    </g>
  </svg>
);

// Crossed-out eye: shown while the password is visible (click to hide).
const IconEyeClosed = () => (
  <svg
    className={css.eyeIcon}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <g
      fill="none"
      fillRule="evenodd"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.9 5.8A8.9 8.9 0 0 1 12 5.5c6.6 0 10.3 6.5 10.3 6.5a17.6 17.6 0 0 1-3.1 3.9M5.9 7.9A17.6 17.6 0 0 0 1.7 12S5.4 18.5 12 18.5a9.6 9.6 0 0 0 3.9-.8" />
      <path d="M10.6 10.6a2 2 0 1 0 2.8 2.8" />
      <path d="M3.5 3.5l17 17" />
    </g>
  </svg>
);

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    unitFieldName,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    isSelect,
    showPasswordToggle,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { input: unitInput } = useField(unitFieldName, {
    subscription: { value: true },
  });

  // Password visibility toggle. Default state is always masked.
  const [passwordVisible, setPasswordVisible] = useState(false);
  const hasPasswordToggle = showPasswordToggle && input.type === 'password';

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
      [css.inputWithPasswordToggle]: hasPasswordToggle,
    });
  const maxLength = CONTENT_MAX_LENGTH;
  const inputProps = isTextarea
    ? {
        className: inputClasses,
        id,
        rows: 1,
        maxLength,
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
      }
    : isUncontrolled
    ? {
        className: inputClasses,
        id,
        type,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
      }
    : { className: inputClasses, id, type, ...refMaybe, ...input, ...rest };

  // The toggle swaps the rendered type; `input.type` itself stays 'password'.
  const resolvedInputProps = hasPasswordToggle
    ? { ...inputProps, type: passwordVisible ? 'text' : 'password' }
    : inputProps;

  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      {isTextarea ? (
        <ExpandingTextarea {...inputProps} />
      ) : (
        <>
          {hasPasswordToggle ? (
            <div className={css.inputBox}>
              <input {...resolvedInputProps} />
              <button
                type="button"
                className={css.passwordToggle}
                onClick={() => setPasswordVisible(prev => !prev)}
                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                aria-pressed={passwordVisible}
                tabIndex={0}
              >
                {passwordVisible ? <IconEyeClosed /> : <IconEyeOpen />}
              </button>
            </div>
          ) : isSelect ? (
            <div className={css.inputBox}>
              <input {...inputProps} />
              {isSelect ? (
                <select
                  className={css.homeSizeDropdown}
                  id={`${id}_unit`}
                  {...unitInput}
                >
                  <option value={'ft²'}>ft²</option>
                  <option value={'m²'}>m²</option>
                </select>
              ) : null}
            </div>
          ) : (
            <input {...inputProps} />
          )}
        </>
      )}
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
  showPasswordToggle: false,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Renders a clickable eye icon that toggles a password field between
  // masked and plain text. Only applies when type is 'password'.
  showPasswordToggle: bool,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    const { isSelect, ...restProps } = this.props;
    return (
      <Field
        component={FieldTextInputComponent}
        isSelect={isSelect}
        {...this.props}
      />
    );
  }
}

export default FieldTextInput;
